<template>
	<vs-popup :title="$t('Affecter un collaborateur')" :active.sync="localVisible">
		<div class="ml-2 mb-4">
			<span> {{ $t("Sélectionner des entreprises pour les collaborateurs suivants") }} : </span>
			<ul>
				<li v-for="(collaborator, index) in collaborators" :key="index" class="text-sm ml-6">
					<span> – </span>
					<span class="pl-2"> {{ collaborator.user.firstname }} {{ collaborator.user.lastname }} </span>
				</li>
			</ul>
		</div>

		<div class="w-full border-solid border rounded-lg overflow-y-auto" style="border-color: var(--border)">
			<table class="w-full border-collapse border-spacing-0">
				<thead class="text-xs bg-grey-light border-b">
					<tr class="border-spacing-0">
						<th class="px-4 pt-2 text-md text-left" style="width: 48px">
							<input
								v-model="select_all_checked_model"
								class="w-5 h-5 cursor-pointer"
								type="checkbox"
								@change="toggleAllApps()"
							/>
						</th>
						<th class="px-4 pt-2 text-md text-left w-200">{{ $t("Nom") }}</th>
						<th class="px-4 pt-2 text-md font-semibold text-center w-200">
							{{ $t("Collaborateurs") }}
						</th>
					</tr>
				</thead>

				<tbody class="w-full" :class="{ 'opacity-50': is_loading }">
					<tr v-for="(item, index_c) in loadedCases" :key="index_c" class="border-b">
						<td class="px-4 flex">
							<div class="flex items-center justify-center" style="height: 40.5px">
								<input
									type="checkbox"
									class="w-5 h-5 cursor-pointer"
									:checked="isAppSelected(item)"
									@change="toggleSelectionApps(item)"
								/>
							</div>
						</td>

						<td class="pr-4 py-1" :style="{ 'max-width': '200px' }">
							<vs-tooltip
								:text="'app_id: ' + item.id + ' | admin: ' + item.admin.name + ' | ' + item.admin.email"
							>
								<div class="flex items-center">
									<img
										:src="getImageUrl(item.organization)"
										class="rounded-full"
										height="28px"
										width="28px"
										:alt="$t('Logo de l’entreprise')"
										style="object-fit: contain"
									/>

									<p class="pl-2 text-left text-md truncate">
										{{ item.organization.name }}
									</p>
								</div>
							</vs-tooltip>
						</td>

						<td class="text-center px-4">
							<div class="flex justify-center items-center relative">
								<span
									v-if="getCollaborators(item.policies, loadedCollaborators).length == 0"
									class="text-grey text-sm italic"
								>
									{{ $t("Aucun collaborateur") }}
								</span>

								<template v-else>
									<vs-tooltip
										v-for="(policy, index_policy) in getCollaborators(
											item.policies,
											loadedCollaborators
										)"
										:key="'index_policy_' + index_policy"
										:color="stringToHSLColor(policy.user.name)"
										:text="`${policy.user.name} – ${$t(policy.policy_profile.name)}`"
									>
										<div
											class="rounded-full shadow-md text-white uppercase text-lg font-medium"
											:style="{
												'background-color': stringToHSLColor(policy.user.name),
												width: '28px',
												height: '28px',
												'margin-left': '-8px',
												'line-height': '28px'
											}"
										>
											{{ policy.user.name.substring(0, 1) }}
										</div>
									</vs-tooltip>
								</template>
							</div>
						</td>
					</tr>

					<tr v-if="loadedCases == null || loadedCases.length == 0">
						<td colspan="7" class="text-center py-4 italic text-grey-light">
							{{ $t("Aucun dossier trouvé") }}
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<div class="mt-4 flex justify-between items-center w-full">
			<select
				v-model="localPageSize"
				class="flex items-center justify-between border px-3 py-2 text-sm"
				:style="{
					width: '70px',
					'border-color': 'var(--border)',
					'background-color': 'transparent',
					'border-radius': '6px !important'
				}"
				name="page_size"
				:disabled="is_loading"
				@change="updatePageSize"
			>
				<option v-for="size in [5, 10, 25]" :key="size" :value="size">
					<span>{{ $t(size) }}</span>
				</option>
			</select>

			<div class="flex items-center justify-end">
				<s-button
					icon=""
					type="border-small"
					class="h-8 flex justify-center items-center text-xs px-3 py-1.5"
					:style="{ 'border-radius': '6px !important' }"
					:disabled="offset === 0 || is_loading"
					@click="previousPage"
				>
					{{ $t("Précédent") }}
				</s-button>

				<s-button
					icon=""
					type="border-small"
					class="h-8 flex justify-center items-center text-xs"
					:style="{ 'border-radius': '6px !important', 'margin-left': '8px !important' }"
					:disabled="offset + localPageSize >= totalRowsLicense || is_loading"
					@click="nextPage"
				>
					{{ $t("Suivant") }}
				</s-button>
			</div>
		</div>

		<div class="flex flex-row items-center justify-center w-full mt-4" style="gap: 12px">
			<s-button
				type="border"
				class="sm:m-base float-none sm:float-left h-8 px-4 py-2 text-sm"
				style="margin: 0 !important"
				@click="$emit('close')"
			>
				{{ $t("Annuler") }}
			</s-button>

			<s-button
				class="sm:m-base float-none sm:float-right h-8 px-4 py-2 text-sm"
				:disabled="loading_save_accountant || (apps_to_delete.length === 0 && apps_to_add.length === 0)"
				style="margin: 0 !important"
				@click="sendInvitation"
			>
				{{ $t("Enregistrer") }}
			</s-button>
		</div>
	</vs-popup>
</template>

<script>
	import { mapGetters, mapState } from "vuex";
	import debounce from "lodash/debounce";

	export default {
		name: "ModalAssignCollaboratorsToCompanies",
		props: {
			visible: {
				type: Boolean,
				default: false
			},
			app: {
				type: Object,
				default: () => {}
			},
			collaborators: {
				type: Array,
				default: () => []
			},
			partnerId: {
				type: Number,
				default: null
			}
		},
		data() {
			return {
				localVisible: this.visible,
				policies: [],
				selectedApps: [],
				selectedUserIndex: null,
				selectedUser: null,
				loading_save_accountant: false,
				is_loading: true,
				localPageSize: this.$store.state.pageSize || 10,
				offset: 0,
				debouncedSearch: null,
				abortController: null,
				SetToggledAllApps: null, // Set object for faster loops
				select_all_checked_model: false, // model for select all checkbox
				alreadySelectedApps: [] // apps having policies that contains one of the collaborators
			};
		},
		computed: {
			...mapGetters(["getCollaborators", "getImageUrl", "stringToHSLColor"]),
			...mapState([
				"loadedPolicies",
				"loadedCases",
				"loadedCollaborators",
				"user",
				"pageSize",
				"totalRowsLicense"
			]),
			apps_to_add() {
				return this.selectedApps.filter(app => !this.alreadySelectedApps.find(app2 => app2.id === app.id));
			},
			apps_to_delete() {
				return this.alreadySelectedApps.filter(app => !this.selectedApps.find(app2 => app2.id === app.id));
			}
		},
		watch: {
			loadedCases: {
				handler(newCases) {
					if (newCases && newCases.length > 0) {
						this.is_loading = false;
						// check if select all chechkbox must be checked
						if (this.SetToggledAllApps !== null) {
							this.select_all_checked_model = newCases.every(c => this.SetToggledAllApps.has(c.id));
						}
						// fill selected_apps & alreadySelectedApps if case has one of the collaborators in his policies
						newCases.forEach(app => {
							if (
								this.collaborators.some(
									collaborator =>
										collaborator.apps_by_policies.find(app2 => app2.app_id === app.id) !== undefined
								)
							) {
								this.selectedApps.push(app);
								this.alreadySelectedApps.push(app);
							}
						});
					}
				},
				immediate: true
			},
			visible: {
				handler(newVal) {
					this.localVisible = newVal;
				},
				immediate: true
			},
			localVisible: {
				handler(newVal) {
					this.$emit("update:visible", newVal);
				}
			},
			localPageSize: {
				handler(newVal) {
					this.$store.commit("SET_PAGE_SIZE", newVal);
				},
				immediate: true
			}
		},
		created() {
			this.debouncedSearch = debounce(this.fetchPartnerCases, 500);
		},
		methods: {
			async sendInvitation() {
				if (!this.apps_to_add.length && !this.apps_to_delete.length) {
					return this.$vs.notify({
						text: this.$t("Aucune modification détectée."),
						color: "danger",
						position: "bottom-right"
					});
				}

				this.loading_save_accountant = true;
				const allInvites = [];
				for (const app of this.apps_to_add) {
					for (const collaborator of this.collaborators) {
						if (collaborator.policy_profile.name === "Support technique") {
							const payload = {
								partnerId: this.user?.partner_id,
								id: collaborator.id,
								appId: app.id
							};
							allInvites.push(this.$store.dispatch("assignSupportCollaborator", payload));
						} else {
							const payload = {
								appId: app.id,
								email: collaborator.user.email,
								policyProfileId: collaborator.policy_profile_id,
								firstname: encodeURIComponent(collaborator.user.firstname),
								lastname: encodeURIComponent(collaborator.user.lastname)
							};
							allInvites.push(this.$store.dispatch("sendInviteMail", payload));
						}
					}
				}
				for (const app of this.apps_to_delete) {
					for (const collaborator of this.collaborators) {
						const payload = {
							appId: app.id,
							userId: collaborator.user_id
						};

						allInvites.push(this.$store.dispatch("deleteAccess", payload));
					}
				}

				try {
					await Promise.all(allInvites);
					this.$vs.notify({
						text: this.$t("Toutes les invitations ont été envoyées."),
						color: "success",
						position: "bottom-right"
					});
				} catch (error) {
					this.$vs.notify({
						text:
							this.$t(error.response?.data?.message) ||
							this.$t("Erreur lors de l’envoi d’une ou plusieurs invitations."),
						color: "danger",
						position: "bottom-right"
					});
				} finally {
					this.loading_save_accountant = false;
					//this.selectedApps = [];
					//this.loadedCases.forEach(item => (item.selected = false));

					this.$emit("update");
				}
			},
			isAppSelected(app) {
				return this.selectedApps.some(item => item.id === app.id);
			},
			toggleSelectionApps(app) {
				const isSelected = this.selectedApps.some(item => item.id === app.id);
				if (isSelected) this.selectedApps = this.selectedApps.filter(item => item.id !== app.id);
				else this.selectedApps.push(app);
			},
			toggleAllApps() {
				// offset = 0 never clicked
				if (this.SetToggledAllApps === null) {
					this.SetToggledAllApps = new Set(this.loadedCases.map(item => item.id));
					this.selectedApps = [...this.loadedCases];
				}
				// unselect for this offset
				else if (!this.select_all_checked_model) {
					this.loadedCases.forEach(c => {
						this.SetToggledAllApps.delete(c.id);
						this.selectedApps = this.selectedApps.filter(item => item.id !== c.id);
					});
				} else if (this.select_all_checked_model) {
					this.loadedCases.forEach(c => {
						this.SetToggledAllApps.add(c.id);
						this.selectedApps.push(c);
					});
				}
			},
			async fetchPartnerCases() {
				this.is_loading = true;

				if (this.abortController) {
					this.abortController.abort();
				}

				this.abortController = new AbortController();
				const { signal } = this.abortController;

				try {
					await this.$store.dispatch("getPartnerCases", {
						partnerId: this.user?.partner_id,
						pageSize: this.localPageSize,
						search: this.search,
						offset: this.offset,
						signal: signal
					});
				} catch (error) {
					if (error.name === "AbortError") {
						console.log("Previous request canceled");
					} else {
						console.error("Error fetching partner cases:", error);
					}
				} finally {
					this.is_loading = false;
				}
			},
			async updatePageSize(event) {
				const newPageSize = parseInt(event.target.value, 10);
				this.$store.commit("SET_PAGE_SIZE", newPageSize);
				this.offset = 0;
				this.debouncedSearch();
			},
			async previousPage() {
				if (this.offset - this.localPageSize < 0) return;

				this.select_all_checked_model = false;
				this.offset -= this.localPageSize;
				this.debouncedSearch();
			},
			async nextPage() {
				if (this.offset + this.localPageSize >= this.totalRowsLicense) return;

				this.select_all_checked_model = false;
				this.offset += this.localPageSize;
				this.debouncedSearch();
			}
		}
	};
</script>

<style scoped>
	.text-grey {
		color: var(--grey);
	}
	.modal-mask {
		position: fixed;
		z-index: 9998;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-color: rgba(0, 0, 0, 0.5);
		display: table;
		transition: opacity 0.3s ease;
	}

	.modal-wrapper {
		display: table-cell;
		vertical-align: middle;
	}

	.modal-container {
		width: 300px;
		margin: 0px auto;
		padding: 20px 30px;
		background-color: #fff;
		border-radius: 2px;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
		transition: all 0.3s ease;
		font-family: Helvetica, Arial, sans-serif;
	}
</style>

<style>
	.vs-tooltip {
		z-index: 99999 !important;
	}
</style>
